import { SubscriptionDetailsResponse } from "@9amhealth/openapi";
import { Cubit } from "blac-next";
import { Props } from "components/WithAuth/WithAuth";
import { PayerId } from "src/constants/payers";
import { authenticationState, subscriptionState } from "src/state/state";
import { AuthStage } from "src/types/auth";

export enum View {
  Loading = "loading",
  Locked = "locked",
  Reauth = "reauth",
  Login = "login",
  App = "app"
}

export interface WithAuthState {
  view: View;
}

export enum AuthenticationMethod {
  ANONYMOUS = "ANONYMOUS",
  PASSWORD = "PASSWORD",
  ROLE_SWITCH = "ROLE_SWITCH",
  EXISTING_TOKEN = "EXISTING_TOKEN",
  GOOGLE = "GOOGLE",
  SAML2 = "SAML2",
  TRANSCARENT_SSO = "TRANSCARENT_SSO"
}

export interface Dependencies {
  props: Props;
  navigate: (path: string, options?: { replace: boolean }) => void;
  allSubscriptions?: SubscriptionDetailsResponse[];
  payerId?: string;
  sessionAuthenticationMethod?: string;
  authStage: AuthStage;
  isInitialCheckComplete: boolean;
  hasStoredCredentials: boolean;
  isBiometricEnabled?: boolean;
  isBiometricVerified?: boolean;
  requestedEmailLogin?: boolean;
}

export class WithAuthCubit extends Cubit<WithAuthState, Props> {
  componentProps: Props;
  dependencies?: Dependencies;

  constructor(props: Props) {
    super({
      view: View.Loading
    });

    this.componentProps = props;
  }

  public readonly checkPassedAuthCheck = (): boolean => {
    const { hasActiveSubscription } = subscriptionState;

    if (this.dependencies?.authStage === "fullAuth") {
      return true;
    }

    if (
      this.dependencies?.authStage === "anonymous" &&
      this.componentProps.createAnonymousUser
    ) {
      return true;
    }

    // for funnel when user is allowed to have an email but not a password, or when the user has a subscription
    if (
      this.dependencies?.authStage === "partialAuth" &&
      (this.componentProps.allowPartialAuth || hasActiveSubscription)
    ) {
      return true;
    }

    return false;
  };

  /**
   * Returns 'pass' if the user has a transcarent payer and is authenticated with a valid sessionAuthenticationMethod
   * Returns 'fail' if the user has a transcarent payer and is not authenticated with a valid sessionAuthenticationMethod
   * Returns 'missing-data' if the payerId or sessionAuthenticationMethod is undefined
   */
  public readonly checkPassedTranscarentAuthCheck = (): "pass" | "fail" => {
    const { payerId, sessionAuthenticationMethod } = this.dependencies ?? {};

    const isTranscarentPayerId = payerId === PayerId.TRANSCARENT;
    if (!isTranscarentPayerId) {
      return "pass";
    }

    const isSamlAuthenticated =
      sessionAuthenticationMethod &&
      [
        AuthenticationMethod.SAML2,
        AuthenticationMethod.TRANSCARENT_SSO
      ].includes(sessionAuthenticationMethod as AuthenticationMethod);

    return isSamlAuthenticated ? "pass" : "fail";
  };

  get checkPassedSubscriptionCheck(): boolean {
    if (this.componentProps.requiresSubscription !== true) {
      return true;
    }

    const { checkHasPlan, getFullSubscription } = subscriptionState;
    const subscription = getFullSubscription();
    const userHasTreatmentPlan = checkHasPlan(subscription?.id);

    return Boolean(userHasTreatmentPlan);
  }

  public setView = (): void => {
    const {
      isBiometricEnabled,
      isBiometricVerified,
      isInitialCheckComplete,
      hasStoredCredentials,
      requestedEmailLogin
    } = this.dependencies ?? {};

    // PHASE 1: Biometric lock handling (before loading state)
    // Check email login fallback first (user requested to bypass biometrics)
    if (requestedEmailLogin) {
      this.patch({ view: View.Login });
      return;
    }

    // Show lock screen immediately if biometrics enabled and session not verified
    // This triggers biometric prompt before waiting for credential loading
    if (isBiometricEnabled && hasStoredCredentials && !isBiometricVerified) {
      this.patch({ view: View.Locked });
      return;
    }

    // PHASE 2: Loading states
    if (!isInitialCheckComplete) {
      this.patch({ view: View.Loading });
      return;
    }

    // PHASE 3: Authentication checks
    const passAuthCheck = this.checkPassedAuthCheck();

    // Handle anonymous user creation
    if (this.componentProps.createAnonymousUser && !passAuthCheck) {
      void authenticationState.createAnonymousAccount();
      this.patch({ view: View.Loading });
      return;
    }

    // Show login if not authenticated
    if (!passAuthCheck) {
      this.patch({ view: View.Login });
      return;
    }

    // PHASE 4: Provider-specific checks
    // Transcarent re-auth check (only when we have required data)
    const allRequiredDataAvailable = Boolean(
      this.dependencies?.sessionAuthenticationMethod
    );
    if (
      allRequiredDataAvailable &&
      this.checkPassedTranscarentAuthCheck() === "fail"
    ) {
      this.patch({ view: View.Reauth });
      return;
    }

    // PHASE 5: Authenticated - show app
    this.patch({ view: View.App });
  };

  initializeDependencies = (deps: Dependencies): void => {
    this.dependencies = deps;
    this.componentProps = deps.props;

    this.setView();
  };
}
